import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { accountApi, AddressRec } from '@netiva/classifieds-api';
import { useAppSelector } from '@/store';
import { useMatomoTagManager } from '@/hooks';

/** matomo tracking functionality, since edit address modal is used in multiple context, tracking needs to be separated */

export type AddressTrackingContext = 'adStepPage' | 'addressPage' | 'homePage';

type UseAddressOptions = {
  trackingContext?: AddressTrackingContext;
  trackingAction?: string;
};

const getAddressPageTrackingAction = (address: AddressRec) => {
  const isMain = address.type === 'Account';
  const isUpdate = address.id > 0;

  if (isMain) {
    return isUpdate ? 'Update_Main_Address' : 'Add_Main_Address';
  }

  return isUpdate ? 'Update_Secondary_Address' : 'Add_Secondary_Address';
};

const getAdStepPageTrackingAction = (address: AddressRec, trackingActionPrefix?: string) => {
  const isUpdate = address.id > 0;
  return isUpdate ? `${trackingActionPrefix}_Edit_Address` : `${trackingActionPrefix}_Create_Address`;
};

const getHomePageTrackingAction = (_: AddressRec) => {
  return ''; // no tracking set up for the homepage implementation
};

const getDeleteTrackingAction = (trackingContext: AddressTrackingContext, trackingActionPrefix?: string) => {
  if (trackingContext === 'addressPage') return 'Delete_Secondary_Adress';
  if (trackingContext === 'adStepPage') return `${trackingActionPrefix}_Delete_Address`;

  return ''; // no tracking set up for the homepage implementation
};

const getTrackingAction = (
  address: AddressRec,
  trackingContext: AddressTrackingContext,
  trackingActionPrefix?: string
) => {
  return trackingActionMap[trackingContext](address, trackingActionPrefix);
};

const trackingCategoryMap: Record<AddressTrackingContext, string> = {
  homePage: '',
  addressPage: 'Interaction_PI-Profile',
  adStepPage: 'Interaction_PI-Insertion',
};

const trackingActionMap: Record<
  AddressTrackingContext,
  (address: AddressRec, trackingActionPrefix?: string) => string
> = {
  addressPage: getAddressPageTrackingAction,
  homePage: getHomePageTrackingAction,
  adStepPage: getAdStepPageTrackingAction,
};

/** // */

export const useAddress = ({
  trackingContext = 'homePage',
  trackingAction: trackingActionPrefix,
}: UseAddressOptions) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { account } = useAppSelector((state) => state.account);

  const { trackMatomoEvent } = useMatomoTagManager();

  const [createAddressApi, { isLoading: isCreating }] = accountApi.useCreateAddress();
  const [updateAddressApi, { isLoading: isUpdating }] = accountApi.useUpdateAddress();
  const [deleteAddressApi, { isLoading: isDeleting }] = accountApi.useDeleteAddress();
  const [createAccount, { isLoading: isCreatingAccount }] = accountApi.useCreateAccount();

  const isLoading = isCreating || isUpdating || isDeleting || isCreatingAccount;

  const saveAddress = useCallback(
    async (address: AddressRec) => {
      try {
        if (address.id > 0) {
          await updateAddressApi({
            id: address.id,
            accountAddressesUpdateAddressRequest: {
              address,
            },
          }).unwrap();
        } else {
          if (account) {
            await createAddressApi({
              accountAddressesCreateAddressRequest: { address },
            }).unwrap();
          } else {
            await createAccount({
              accountAccountCreateAccountRequest: { address },
            }).unwrap();
          }
        }

        //console.log(trackingActionPrefix);

        const trackingAction = getTrackingAction(address, trackingContext, trackingActionPrefix);

        trackMatomoEvent({
          trackingCategory: trackingCategoryMap[trackingContext],
          trackingAction,
        });
        toast({ status: 'success', description: t('address.save.success') });
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    [
      trackingActionPrefix,
      trackingContext,
      trackMatomoEvent,
      toast,
      t,
      updateAddressApi,
      account,
      createAddressApi,
      createAccount,
    ]
  );

  const deleteAddress = useCallback(
    async (id: number) => {
      try {
        await deleteAddressApi({ id }).unwrap();
        trackMatomoEvent({
          trackingCategory: trackingCategoryMap[trackingContext],
          trackingAction: getDeleteTrackingAction(trackingContext),
        });
        toast({ status: 'success', description: t('address.delete.success') });
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    [deleteAddressApi, t, toast, trackMatomoEvent, trackingContext]
  );

  return {
    saveAddress,
    deleteAddress,
    isLoading,
  };
};
